import { useState } from "react";
import { formatNZDate, formatNZDateTime } from "../../utils/timezone";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Trash2, DollarSign, Store, User } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  rejected: "bg-gray-500/10 text-gray-600 border-gray-500/30",
};

export function AdminQuotesTab({ quotes, loading, onAdminDelete }) {
  const [deleteDialog, setDeleteDialog] = useState({ open: false, quote: null });
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!reason.trim()) return;
    setDeleting(true);
    await onAdminDelete(deleteDialog.quote.quote_id, reason.trim());
    setDeleting(false);
    setDeleteDialog({ open: false, quote: null });
    setReason("");
  };

  return (
    <>
    <Card className="glass-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          All Quotes ({quotes.length})
        </CardTitle>
        <CardDescription>Every quote submitted by shops — delete with reason to notify the shop by email</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
        ) : quotes.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No quotes submitted yet</p>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quote ID</TableHead>
                <TableHead>Request</TableHead>
                <TableHead>Shop</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Tyre</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => (
                <TableRow key={q.quote_id} data-testid={`admin-quote-row-${q.quote_id}`}>
                  <TableCell className="font-mono text-xs">{q.quote_number || `#${q.quote_id?.substring(0, 8)}`}</TableCell>
                  <TableCell className="font-mono text-xs">{q.request_number || `#${q.request_id?.substring(0, 8)}`}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Store className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm">{q.shop_name || "Unknown"}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm">{q.customer_name || "—"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{q.tyre_brand} {q.tyre_model}</TableCell>
                  <TableCell className="font-semibold text-primary">${q.total_price?.toFixed(2)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{q.quote_expiry ? formatNZDate(q.quote_expiry) : "—"}</TableCell>
                  <TableCell>
                    {q.deletion_status === "pending" ? (
                      <Badge className="bg-red-500/10 text-red-600 border-red-500/30">Deletion Pending</Badge>
                    ) : q.deletion_status === "rejected" ? (
                      <Badge className={STATUS_COLORS.rejected}>Deletion Declined</Badge>
                    ) : q.is_selected ? (
                      <Badge className="bg-green-500/10 text-green-600 border-green-500/30">Accepted</Badge>
                    ) : (
                      <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatNZDate(q.created_at)}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive h-8 w-8 p-0"
                      onClick={() => setDeleteDialog({ open: true, quote: q })}
                      data-testid={`admin-delete-quote-${q.quote_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Admin Delete Quote Dialog */}
    <Dialog open={deleteDialog.open} onOpenChange={(open) => { if (!open) { setDeleteDialog({ open: false, quote: null }); setReason(""); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-destructive flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Delete Quote
          </DialogTitle>
          <DialogDescription>
            This will permanently delete the quote and send an email notification to the shop explaining the reason.
          </DialogDescription>
        </DialogHeader>

        {deleteDialog.quote && (
          <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-1">
            <p><span className="text-muted-foreground">Quote:</span> <span className="font-semibold">#{deleteDialog.quote.quote_id?.substring(0, 8)}</span></p>
            <p><span className="text-muted-foreground">Shop:</span> {deleteDialog.quote.shop_name || "Unknown"}</p>
            <p><span className="text-muted-foreground">Tyre:</span> {deleteDialog.quote.tyre_brand} {deleteDialog.quote.tyre_model}</p>
            <p><span className="text-muted-foreground">Price:</span> <span className="font-semibold text-primary">${deleteDialog.quote.total_price?.toFixed(2)}</span></p>
          </div>
        )}

        <div className="space-y-2">
          <Label>Reason for deletion <span className="text-destructive">*</span></Label>
          <Textarea
            placeholder="Explain why this quote is being removed..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            data-testid="admin-delete-reason-input"
          />
          {!reason.trim() && reason !== "" && (
            <p className="text-xs text-destructive">A reason is required</p>
          )}
          <p className="text-xs text-muted-foreground">The shop will receive an email with this reason.</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { setDeleteDialog({ open: false, quote: null }); setReason(""); }}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleting || !reason.trim()} data-testid="confirm-admin-delete-quote-btn">
            {deleting ? "Deleting..." : "Delete & Notify Shop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
